import { ValueFormatterParams } from "ag-grid-community";
import React, { useCallback, useRef, useState } from "react";
import PlusButtonIcon from "../../../../assets/images/PlusButtonIcon.svg";
import Grid from "../../../../components/Grid";
import { ColumnDef } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import { formatDateBR, formatDateTimeBR } from "../../../../helpers/format";
import { useModal } from "../../../../hooks/ModalContext";
import { useAuth } from "../../../../hooks/AuthenticateContext";
import api from "../../../../services/api";
import Create from "./Create";
import Info from "./Info";
import { IOperacoesPatioEntradaSaidaVeiculosSPE } from "./types/types";
import ModalHistory from "../../../../components/ModalHistory";
import Can from "../../../../components/Can";

const OperacoesPatioEntradaSaidaVeiculosSPE: React.FC = () => {
  const [columns] = useState<ColumnDef[]>([
    {
      field: "data_hora",
      headerName: "Data/Hora Entrada",
      filter: true,
      type: "dateColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return formatDateTimeBR(params.value);
        }
        return "---";
      },
    },
    {
      field: "placa_dianteira",
      headerName: "Placa Dianteira",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value.toUpperCase();
        }
        return "---";
      },
    },
    {
      field: "cancela.numero_cancela",
      headerName: "Cancela Entrada",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "saida.data_hora",
      headerName: "Data/Hora Saída",
      filter: true,
      type: "dateColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return formatDateTimeBR(params.value);
        }
        return "---";
      },
    },
    {
      field: "saida.placa_dianteira",
      headerName: "Placa Dianteira Saída",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value.toUpperCase();
        }
        return "---";
      },
    },
    {
      field: "usuario",
      headerName: "Usuário",
      filter: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "data_historico",
      headerName: "Data de Modificação",
      filter: true,
      filterParams: {
        dateBetween: true,
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return formatDateBR(params.value);
        }
        return "---";
      },
    },
  ]);

  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<IOperacoesPatioEntradaSaidaVeiculosSPE>();
  const [isView, setIsView] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isHistory, setIsHistory] = useState<boolean>(false);

  const { isModalOpen, openModal, closeModal } = useModal();
  const { user } = useAuth();
  const gridRef = useRef<any>();

  const onDelete = useCallback(async (rowId?: number) => {
    try {
      setLoading(true);
      const body = {
        id_operacao_patio_entrada_veiculo: rowId,
        id_usuario_historico: user?.id,
      };

      await api.post("/deletar/entradaVeiculosSpe", body);
      setLoading(false);
      setIsRemove(false);
      gridRef.current?.refresh();
    } catch {
      setLoading(false);
    }
  }, [user]);

  return (
    <div className="flex flex-col w-full h-screen bg-[#F5F5F5] p-5">
      <Loading loading={loading} />
      {isModalOpen && (
        <Create
          isEdit={isEdit}
          isView={isView}
          selectedRow={selectedRow}
          onClear={() => closeModal()}
          onConfirm={() => {
            closeModal();
            gridRef.current?.refresh();
          }}
        />
      )}

      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(false)}
          onConfirm={() => onDelete(selectedRow?.id_operacao_patio_entrada_veiculo)}
          row={selectedRow?.placa_dianteira || String(selectedRow?.id_operacao_patio_entrada_veiculo)}
        />
      )}

      {isView && (
        <Info
          data={selectedRow}
          title="Detalhes - Entrada e Saída de Veículos SPE"
          onClose={() => setIsView(false)}
        />
      )}

      {isHistory && selectedRow && (
        <ModalHistory
          isOpen={isHistory}
          title="Histórico - Entrada e Saída de Veículos SPE"
          historyRoute="/historico/entradaVeiculosSpe"
          bodyKey="id_operacao_patio_entrada_veiculo"
          bodyValue={selectedRow.id_operacao_patio_entrada_veiculo ?? 0}
          onClose={() => setIsHistory(false)}
        />
      )}

      <div className="flex items-center justify-between w-full mb-3">
        <div>
          <h1 className="text-2xl text-[#000000] font-bold">Entrada/Saída Veículos SPE</h1>
        </div>
        <div className="mr-14 flex items-center gap-4">
          <Can acao="SALVAR">
          <button
            className="flex items-center justify-center h-12 w-36 bg-[#062D4E] text-[#FFFFFF] text-sm font-light border-none rounded-full"
            onClick={() => {
              setSelectedRow(undefined);
              setIsEdit(false);
              setIsView(false);
              openModal();
            }}
            id="btnCreateEntradaSaidaSPE"
          >
            Adicionar <img src={PlusButtonIcon} alt="" className="ml-2" />
          </button>
          </Can>
        </div>
      </div>
      <div className="flex w-full">
        <Grid
          ref={gridRef}
          columns={columns}
          filters={[]}
          status={[]}
          pagination
          path="/listar/entradaVeiculosSpe"
          onDelete={(data: any) => {
            setIsRemove(true);
            setSelectedRow(data);
          }}
          isShowStatus={false}
          onView={(data: any) => {
            setSelectedRow(data);
            setIsView(true);
          }}
          onUpdate={(data: any) => {
            setSelectedRow(data);
            setIsEdit(true);
            setIsView(false);
            openModal();
          }}
          onHistory={(data: any) => {
            setSelectedRow(data);
            setIsHistory(true);
          }}
        />
      </div>
    </div>
  );
};

export default OperacoesPatioEntradaSaidaVeiculosSPE;
